import React, { lazy, Suspense, useMemo } from "react";
import copy from "copy-to-clipboard";
import { Dialog } from "@app/shared/src/components/Dialog";
import { IconLink, Icons } from "@app/dashboard/src/components/shared";

// react-json-view gives a collapsible tree with per-node copy buttons — reused for both response
// headers (parsed into a name→value object) and JSON bodies. Lazy so it stays out of the main chunk.
const JsonViewer = lazy(() => import("@app/dashboard/src/components/shared/JsonViewer"));

export type HistoryContent = { title: string; kind: "headers" | "body"; content: string };

// Response headers reach us as a JSON array of { Key, Value } (the firmware serializes
// HttpResponseHeaders, where Value is a string[]), e.g. [{"Key":"Server","Value":["nginx/1.18.0"]}].
// We normalize that — plus a plain { name: value } object and the older "Name: value" line format —
// into a flat { Name: "v1, v2" } map for both the tree viewer and the inline hover/count.
export const parseHeaders = (raw: string): Record<string, string> => {
    const out: Record<string, string> = {};
    const stringifyValue = (v: unknown): string => (Array.isArray(v) ? v.join(", ") : v == null ? "" : String(v));

    const t = raw.trim();
    if (t.startsWith("[") || t.startsWith("{")) {
        try {
            const parsed = JSON.parse(t);
            if (Array.isArray(parsed)) {
                parsed.forEach((h) => {
                    const key = h?.Key ?? h?.key;
                    if (key != null) out[String(key)] = stringifyValue(h?.Value ?? h?.value);
                });
                return out;
            }
            if (parsed && typeof parsed === "object") {
                Object.entries(parsed).forEach(([k, v]) => (out[k] = stringifyValue(v)));
                return out;
            }
        } catch {
            // fall through to line parsing
        }
    }

    raw.split(/\r?\n/).forEach((line) => {
        const idx = line.indexOf(":");
        if (idx > 0) out[line.slice(0, idx).trim()] = line.slice(idx + 1).trim();
        else if (line.trim()) out[line.trim()] = "";
    });
    return out;
};

// Header map → "Name: value" lines, for the inline hover preview.
export const formatHeaderLines = (raw: string): string =>
    Object.entries(parseHeaders(raw))
        .map(([k, v]) => `${k}: ${v}`)
        .join("\n");

const tryParseJson = (raw: string): unknown | undefined => {
    const t = raw.trim();
    if (!t || !(t.startsWith("{") || t.startsWith("["))) return undefined;
    try {
        return JSON.parse(t);
    } catch {
        return undefined;
    }
};

// Best-effort XML indenter: break between adjacent tags, then indent by depth. Falls back to the
// raw string on anything unexpected — this is for readability, not validation.
const formatXml = (raw: string): string => {
    try {
        const broken = raw.replace(/>\s*</g, ">\n<").trim();
        let depth = 0;
        return broken
            .split("\n")
            .map((line) => {
                const isClose = /^<\//.test(line);
                const isSelfContained = /^<[^>]+\/>$/.test(line) || /^<([\w:.-]+)[^>]*>.*<\/\1>$/.test(line);
                if (isClose) depth = Math.max(depth - 1, 0);
                const out = "  ".repeat(depth) + line;
                const isOpen = /^<[^!?/][^>]*[^/]>$/.test(line);
                if (isOpen && !isSelfContained && !isClose) depth++;
                return out;
            })
            .join("\n");
    } catch {
        return raw;
    }
};

export const HistoryContentDialog: React.FC<{ detail: HistoryContent | null; onClose: () => void }> = ({ detail, onClose }) => {
    const raw = detail?.content ?? "";

    // Headers always render as a tree; a body renders as a tree only if it parses as JSON.
    const treeData = useMemo<unknown | undefined>(() => {
        if (!detail) return undefined;
        return detail.kind === "headers" ? parseHeaders(raw) : tryParseJson(raw);
    }, [detail, raw]);

    const isXml = detail?.kind === "body" && !treeData && raw.trim().startsWith("<");
    const text = isXml ? formatXml(raw) : raw;

    return (
        <Dialog show={!!detail} title={detail?.title ?? ""} onOkClick={onClose} onCancelClick={onClose} okButtonText="Close" showCancelButton={false}>
            {treeData ? (
                <Suspense fallback={<div className="p-2 text-sm">Loading…</div>}>
                    <div className="max-h-[60vh] overflow-auto text-sm">
                        <JsonViewer data={treeData} />
                    </div>
                </Suspense>
            ) : (
                <div className="relative">
                    <IconLink Icon={Icons.Clipboard} className="absolute right-1 top-1" onClick={() => copy(raw)} />
                    <pre className="max-h-[60vh] overflow-auto text-xs whitespace-pre-wrap break-words bg-black/5 rounded p-3 pr-9">
                        {text || "—"}
                    </pre>
                </div>
            )}
        </Dialog>
    );
};
